import { useState, useRef, useEffect } from 'react'
import { useJourneyStore } from '@/stores/journeyStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { ChevronDown, CheckCircle2, Compass } from 'lucide-react'
import { cn } from '@/lib/utils'

export function JourneyDropdown() {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { setShowGoalWizard } = useWorkspaceStore()

  const {
    getSelectedGoal,
    getActivePhases,
    getCurrentPhase,
    getPhaseProgress,
  } = useJourneyStore()

  const selectedGoal = getSelectedGoal()
  const phases = getActivePhases()
  const currentPhase = getCurrentPhase()

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  const handleSwitchJourney = () => {
    setIsOpen(false)
    setShowGoalWizard(true)
  }

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors',
          'border border-warm-200 hover:bg-warm-100',
          isOpen && 'bg-warm-100'
        )}
      >
        <Compass className="w-4 h-4 text-market" />
        <span className="font-medium text-text">
          {selectedGoal ? selectedGoal.name : 'Select Journey'}
        </span>
        <ChevronDown className={cn(
          'w-3.5 h-3.5 text-text-muted transition-transform',
          isOpen && 'rotate-180'
        )} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[420px] bg-white rounded-xl border border-warm-200 shadow-lg z-50 overflow-hidden">
          {/* Phase stepper */}
          {phases.length > 0 && (
            <div className="p-4">
              <div className="text-xs font-medium text-text-muted uppercase tracking-wide mb-3">
                Journey Progress
              </div>
              <div className="flex items-center gap-1">
                {phases.map((phase, index) => {
                  const progress = getPhaseProgress(phase.id)
                  const isCurrent = phase.id === currentPhase?.id
                  const isCompleted = progress >= 100

                  return (
                    <div key={phase.id} className="flex items-center flex-1">
                      {/* Phase node */}
                      <div className="flex flex-col items-center flex-1">
                        <div
                          className={cn(
                            'w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all',
                            isCompleted
                              ? 'bg-market text-white'
                              : isCurrent
                                ? 'text-white'
                                : 'bg-warm-100 text-text-muted'
                          )}
                          style={isCurrent && !isCompleted ? { backgroundColor: phase.color } : undefined}
                        >
                          {isCompleted ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : (
                            phase.order
                          )}
                        </div>
                        <span className={cn(
                          'text-[10px] mt-1 text-center leading-tight',
                          isCurrent ? 'font-medium text-text' : 'text-text-muted'
                        )}>
                          {phase.name}
                        </span>
                      </div>
                      {/* Connector line */}
                      {index < phases.length - 1 && (
                        <div className={cn(
                          'h-0.5 flex-1 -mt-4',
                          isCompleted ? 'bg-market' : 'bg-warm-200'
                        )} />
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Switch journey link */}
          <div className="border-t border-warm-100 px-4 py-3 flex items-center justify-between">
            <span className="text-xs text-text-muted">
              {currentPhase
                ? `Phase ${currentPhase.order}: ${currentPhase.name}`
                : 'No phase active'}
            </span>
            <button
              onClick={handleSwitchJourney}
              className="text-xs font-medium text-business hover:text-business/80 transition-colors"
            >
              {selectedGoal ? 'Switch journey' : 'Select a journey'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
